import { FC, useMemo } from "react";

import styles from "./ReputationIndexPreview.module.scss";
import {
  Preloader,
  LineChart,
  ErrorPlaceholder,
  TimePlotterPlaceholder,
} from "../../../components";
import reputationParameters from "../../../data/reputationParameters";
import { INDEX } from "../../constants";
import { useWidgetFetching } from "../../hooks";
import { usePrepareLineChartData } from "../../../components/widgets/LineChart/hooks";
import { PendingStub } from "../../../components/Tiles/WidgetTile/components/Stubs";

// Inner imports
import { WIDGET_ID } from "./constants";
import {
  SummaryTimePeriod,
  SummaryYearlyAverage,
} from "../../descriptionInsertionComponents";
import { WidgetSummaryProps } from "../../types";

export const ReputationIndexSummary: FC<WidgetSummaryProps> = ({
  searchId,
  timePlot,
  selectedBrand,
}) => {
  const { widgetData, isLoading, isCalculating } = useWidgetFetching(
    searchId,
    WIDGET_ID,
  );

  const { lineChartData, pieChartData } = useMemo(() => {
    const { brandData = {} } =
      widgetData?.find(({ brandName }) => brandName === selectedBrand.name) ||
      {};
    const { lineChart = [], pieChart } = brandData;

    return {
      lineChartData: lineChart,
      pieChartData: pieChart,
    };
  }, [selectedBrand, widgetData]);

  const { data: filteredData } = usePrepareLineChartData({
    data: lineChartData,
    searchId,
    timePlotterId: timePlot.id,
    viewMode: "timePlotterSummary",
  });

  const mainBrandValues = useMemo<number[]>(
    () => filteredData.map(({ values }) => values[INDEX] || 0),
    [filteredData],
  );

  const mainBrandPieChartYearlyValue = useMemo<number>(
    () => pieChartData?.values[INDEX] || 0,
    [pieChartData],
  );

  if (isLoading) return <Preloader type="bar" modifier="inner" />;

  if (isCalculating) return <PendingStub />;

  if (!lineChartData.length) return <ErrorPlaceholder />;

  if (!filteredData.length) return <TimePlotterPlaceholder />;

  return (
    <div className={styles.widgetSummary}>
      <div className={styles.descriptionWrapper}>
        <SummaryTimePeriod
          data={mainBrandValues}
          widgetId={WIDGET_ID}
          selectedBrand={selectedBrand}
        />
        <SummaryYearlyAverage
          data={mainBrandPieChartYearlyValue}
          widgetId={WIDGET_ID}
          selectedBrand={selectedBrand}
        />
      </div>
      <div className={styles.chartWrapper}>
        <LineChart
          className={styles.chart}
          labels={reputationParameters.map(({ name, color }) => ({
            id: name,
            name,
            color,
          }))}
          filteredData={filteredData}
          searchId={searchId}
          timePlotterId={timePlot.id}
          viewMode="timePlotterSummary"
          chartStyles={{
            lineStrokeWidth: 3,
          }}
          chartSettings={{
            hasBackground: true,
          }}
          axis={{
            yAxisVerticalPadding: { top: 10, bottom: 0 },
          }}
        />
      </div>
    </div>
  );
};
